import {
  AttachmentTypeEnum,
  supportedAudioExtensions
} from "@coxauto-ui/cxm/communications/interfaces";
import { ArrowDownTrayIcon } from "@interstate/components/Icons";
import styled from "styled-components";
import AttachmentIcon from "../core/channel-type-attachment-icon";
import {
  attachmentDownloadErrorTestId,
  messageItemAttachmentIcon,
  messageItemAttachmentName
} from "../testIds";
import {
  getCommunicationAttachment,
  getCommunicationAttachmentV2,
  isResponseError
} from "@coxauto-ui/cxm/communications/api";
import { Suspense, useCallback, useContext, useState } from "react";
import { ConversationContext, ConversationDetailsContext } from "../context";
import { Spinner } from "@interstate/components/Spinner";
import { PaperClipIcon } from "@interstate/components/Icons";
import React from "react";
import { useToaster } from "@interstate/components/Toast";
import { ErrorMessages } from "@coxauto-ui/cxm/communications/util";
import { ErrorAlert } from "../components/error-alert";
import type { ErrorDetails } from "../components/error-alert";
import { extractErrorDetails } from "../utils/extract-error-details";
import { useCxmFlags } from "@coxauto-ui/core/third-party/launch-darkly";
import { getMimeType } from "./attachment-mime-utils";
const ItemAttachmentViewLazy = React.lazy(
  () => import("./item-attachment-view")
);
const ItemAttachmentPlayAudioLazy = React.lazy(
  () => import("./item-attachment-play-audio")
);

export const StyledMessageAttachment = styled.div`
  display: flex;
  align-items: center;
`;
export const StyledAttachmentIconContainer = styled.div`
  background-color: #005ba8;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 40px; // dunno if fixing this typo is going to bork anything... it will have not been applied previously
  padding: 12px;
  border-radius: 4px 0px 0px 4px;
  width: 40px;
  flex: none;
  order: 0;
  flex-grow: 0;
`;
export const StyledAttachmentInfoContainer = styled.div`
  width: 100%;
  margin-right: 12px;
  margin-left: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
`;
export const StyledAttachmentName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 90%;
`;

export const StyledSimpleAttachmentContainer = styled.div`
  display: flex;
  align-items: center;
  color: #005ba8;
`;

export const StyledSimpleAttachmentIconContainer = styled.div`
  margin-right: 6px;
  display: flex;
`;

const StyledSimpleAttachmentName = styled.span`
  color: #005ba8;
  cursor: pointer;
  text-decoration: none;
`;

export interface ItemAttachmentProps {
  attachmentId: string;
  attachmentType: AttachmentTypeEnum;
  attachmentName: string;
  coxAutoMessageId: number | string;
  date: string;
  simple?: boolean;
  attachmentBubbleClassName?: string;
  attachmentViewClassName?: string;
  attachmentUrl?: string;
}

export function ItemAttachment({
  attachmentId,
  attachmentType,
  attachmentName,
  coxAutoMessageId,
  simple = false,
  date,
  attachmentBubbleClassName = "",
  attachmentViewClassName = "",
  attachmentUrl
}: ItemAttachmentProps) {
  const { customerId, customerSystem, env, vinDealerId } =
    useContext(ConversationContext);
  const { xSignature } = useContext(ConversationDetailsContext);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadError, setDownloadError] = useState<string>("");
  const [downloadErrorDetails, setDownloadErrorDetails] = useState<
    ErrorDetails | undefined
  >(undefined);
  const { toast: toaster } = useToaster();
  const { "nx.cxm.use-message-details-v2": useMessageDetailsV2 } =
    useCxmFlags();
  const shouldUseV2 = useMessageDetailsV2 === true;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {};

  const getBlob = useCallback(async () => {
    if (!coxAutoMessageId) {
      toaster(
        "Error",
        "This message is still being processed, please try later.",
        noop,
        undefined
      );
      return undefined;
    } else {
      setIsLoading(true);
      setDownloadError("");
      setDownloadErrorDetails(undefined);
      const resultBinary = await getCommunicationAttachment(
        {
          attachmentId,
          consumerId: customerId,
          consumerRecordSystem: customerSystem,
          coxAutoMessageId,
          XSignature: xSignature
        },
        env
      );

      if (isResponseError(resultBinary)) {
        setDownloadError(ErrorMessages.ATTACHMENT_DOWNLOAD_FAILED);
        setDownloadErrorDetails({
          ...extractErrorDetails(resultBinary),
          dealerId: vinDealerId,
          consumerId: customerId
        });
        setIsLoading(false);
        return undefined;
      } else {
        setIsLoading(false);
        return resultBinary;
      }
    }
  }, [
    attachmentId,
    customerId,
    customerSystem,
    coxAutoMessageId,
    xSignature,
    env,
    setDownloadError,
    toaster,
    vinDealerId
  ]);
  const attachmentHandler = useCallback(async () => {
    if (
      shouldUseV2 &&
      attachmentUrl &&
      attachmentType !== AttachmentTypeEnum.VIDEO
    ) {
      setIsLoading(true);
      const extension = attachmentName?.split(".").pop()?.toLowerCase() ?? "";
      const resultBinary = await getCommunicationAttachmentV2(
        {
          communicationId: coxAutoMessageId.toString(),
          attachmentId,
          presignedUrl: attachmentUrl,
          filename: attachmentName,
          contentType: getMimeType(extension),
          consumerId: customerId,
          XSignature: xSignature
        },
        env
      );
      setIsLoading(false);
      if (isResponseError(resultBinary)) {
        setDownloadError(ErrorMessages.ATTACHMENT_DOWNLOAD_FAILED);
        setDownloadErrorDetails({
          ...extractErrorDetails(resultBinary),
          dealerId: vinDealerId,
          consumerId: customerId
        });
        return;
      }
      const blobUrl = window.URL.createObjectURL(
        new Blob([resultBinary], { type: getMimeType(extension) })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", attachmentName);
      const clickHandler = () => {
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
          link.removeEventListener("click", clickHandler);
        }, 150);
      };
      link.addEventListener("click", clickHandler, false);
      document.body.appendChild(link);
      link.click();
      return;
    }
    if (attachmentType == AttachmentTypeEnum.VIDEO && attachmentUrl) {
      // Outbound videos will have a URL to reference.
      const link = Object.assign(document.createElement("a"), {
        target: "_blank",
        rel: "noreferrer",
        href: attachmentUrl
      });

      link.click();
      return;
    }
    const resultBinary = await getBlob();
    if (resultBinary === undefined) return;
    const ext = attachmentName?.split(".").pop()?.toLowerCase() ?? "";
    const url = window.URL.createObjectURL(
      new Blob([resultBinary], { type: getMimeType(ext) })
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", attachmentName);
    const clickHandler = () => {
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        window.removeEventListener("click", clickHandler);
      }, 150);
    };
    link.addEventListener("click", clickHandler, false);
    document.body.appendChild(link);
    link.click();
  }, [
    shouldUseV2,
    attachmentUrl,
    attachmentType,
    getBlob,
    attachmentName,
    coxAutoMessageId,
    attachmentId,
    customerId,
    xSignature,
    env,
    vinDealerId
  ]);
  const attachmentContent = (() => {
    const extension = attachmentName?.split(".").pop()?.toLowerCase() ?? "*";

    if (simple) {
      return (
        <StyledSimpleAttachmentContainer onClick={attachmentHandler}>
          <StyledSimpleAttachmentIconContainer>
            {(isLoading && (
              <Spinner id="conversation-simple-item-attachment-loading-indicator" />
            )) || <PaperClipIcon />}
          </StyledSimpleAttachmentIconContainer>
          <StyledSimpleAttachmentName>
            {attachmentName}
          </StyledSimpleAttachmentName>
        </StyledSimpleAttachmentContainer>
      );
    } else if (attachmentType === AttachmentTypeEnum.IMAGE) {
      return (
        <div className={attachmentViewClassName}>
          <div
            className="attachment-view-container"
            data-testid={messageItemAttachmentName + attachmentId}
          >
            <Suspense>
              <ItemAttachmentViewLazy
                attachmentId={attachmentId}
                coxAutoMessageId={coxAutoMessageId}
                fileExtension={extension}
                filename={shouldUseV2 ? attachmentName : undefined}
                isDownloadLoading={isLoading}
                presignedUrl={shouldUseV2 ? attachmentUrl : undefined}
                onDownload={attachmentHandler}
                onGetBlob={getBlob}
              />
            </Suspense>
          </div>
        </div>
      );
    } else if (
      attachmentType === AttachmentTypeEnum.AUDIO &&
      supportedAudioExtensions.includes(extension)
    ) {
      return (
        <div className={attachmentBubbleClassName}>
          <div
            className="attachment-audio-container"
            data-testid={messageItemAttachmentName + attachmentId}
          >
            <Suspense>
              <ItemAttachmentPlayAudioLazy
                coxAutoMessageId={coxAutoMessageId}
                fileExtension={extension}
                isDownloadLoading={isLoading}
                onDownload={attachmentHandler}
                onGetBlob={getBlob}
              />
            </Suspense>
          </div>
        </div>
      );
    } else if (attachmentType === AttachmentTypeEnum.VIDEO) {
      return (
        <div className={attachmentBubbleClassName} onClick={attachmentHandler}>
          <StyledMessageAttachment>
            <StyledAttachmentIconContainer>
              <AttachmentIcon
                attachmentType={attachmentType}
                data-testid={messageItemAttachmentIcon + attachmentId}
              />
            </StyledAttachmentIconContainer>
            <StyledAttachmentInfoContainer>
              <StyledAttachmentName
                data-testid={messageItemAttachmentName + attachmentId}
              >
                {new Date(date).toLocaleString()}
              </StyledAttachmentName>
            </StyledAttachmentInfoContainer>
          </StyledMessageAttachment>
        </div>
      );
    } else {
      return (
        <div className={attachmentBubbleClassName} onClick={attachmentHandler}>
          <StyledMessageAttachment>
            <StyledAttachmentIconContainer>
              <AttachmentIcon
                attachmentType={attachmentType}
                data-testid={messageItemAttachmentIcon + attachmentId}
              />
            </StyledAttachmentIconContainer>
            <StyledAttachmentInfoContainer>
              <StyledAttachmentName
                data-testid={messageItemAttachmentName + attachmentId}
              >
                {attachmentName}
              </StyledAttachmentName>
              {(isLoading && (
                <Spinner id="conversation-item-attachment-loading-indicator" />
              )) || <ArrowDownTrayIcon id="IconDownload" />}
            </StyledAttachmentInfoContainer>
          </StyledMessageAttachment>
        </div>
      );
    }
  })();

  return (
    <>
      {attachmentContent}
      {downloadError && (
        <ErrorAlert
          details={downloadErrorDetails}
          message={downloadError}
          testId={attachmentDownloadErrorTestId}
        />
      )}
    </>
  );
}
export default ItemAttachment;
